// AI-CONTEXT: See docs/methodiek-impact-per-euro.md § 5.2 en § 6.3
// AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
//
// Wat de gebruiker kan doen zodat een meting wél meetelt voor Impact per euro.
// Nodig omdat 'Telt niet mee' alleen de reden noemde: een organisatie zag
// "Niet doorgerekend: duur of uren onbekend" bij zes effecten en wist niet
// waar ze dat kon invullen (inbox #347). Eén plek per reden, gekoppeld aan de
// vaste codes uit buildLinkedMeasurementBreakdown en bepaalStatus — de
// teksten daar mogen veranderen, de codes niet.
//
// De labels hieronder zijn de exacte UI-labels; hernoem je er een in de app,
// pas het dan hier én in docs/handleiding/dashboards.md aan.

/**
 * @typedef {object} Advies
 * @property {string} tekst - Wat de gebruiker kan doen, in één of twee zinnen
 * @property {{label: string, pad: string}|null} link - Waar dat kan, of null als er niets te doen is of het op deze pagina staat
 */

const NIETS_TE_DOEN = 'Hier hoeft niets te gebeuren:'

const DUUR_UREN = "Vul één keer de standaard in bij 'Instellingen' → 'Activiteiten' → 'Duur en intensiteit van de deelname', "
    + "of per deelnemer 'Startdatum' en 'Uren per week' onder 'Traject' op het tabblad 'Gegevens'."
const EINDE_VOOR_START = "Controleer bij de deelnemer op het tabblad 'Gegevens' onder 'Traject' de 'Startdatum' en de 'Einddatum': "
    + 'de einddatum ligt nu vóór de startdatum.'
const NAAR_DECCOS = 'Neem contact op met Deccos via info@deccos.nl; dit lossen wij voor je op.'

const naarStandaard = (client) => ({ label: 'Naar de standaard', pad: `/${client}/settings/4#settings-duur-intensiteit` })
const naarDeelnemers = (client) => ({ label: 'Naar deelnemers', pad: `/${client}/personas` })
const naarOnderzoeken = (client) => ({ label: 'Naar onderzoeken', pad: `/${client}/research` })
const naarKosten = (client) => ({ label: 'Naar kosten per jaar', pad: `/${client}/settings/1` })

/** @type {Object<string, (client: string) => Advies>} */
const ADVIES = {
    // --- Participatieladder ---
    'geen-meting-in-boekjaar': (client) => ({
        tekst: "Leg in dit boekjaar een meting vast: open de deelnemer, tabblad 'Participatieladder', en werk de huidige situatie bij "
            + "of klik op 'Meting vastleggen'. Kies bij 'Meetdatum' een datum in het boekjaar.",
        link: naarDeelnemers(client),
    }),
    'geen-hoofdactiviteit': (client) => ({
        tekst: "Vul bij de deelnemer op het tabblad 'Participatieladder' bij de huidige situatie 'Wat doe je op dit moment?' in.",
        link: naarDeelnemers(client),
    }),

    // --- Rekenregels: duur en uren ---
    'duur-uren-onbekend': (client) => ({ tekst: DUUR_UREN, link: naarStandaard(client) }),
    'einde-voor-start': (client) => ({ tekst: EINDE_VOOR_START, link: naarDeelnemers(client) }),
    'duur-uren-of-einde': (client) => ({ tekst: `${DUUR_UREN} ${EINDE_VOOR_START}`, link: naarStandaard(client) }),

    // --- MSI-effecten ---
    'telt-al-mee-samengevoegd': () => ({
        tekst: `${NIETS_TE_DOEN} de waarde van dit effect zit al in het effect dat erachter staat.`,
        link: null,
    }),
    'telt-al-mee-ladder': () => ({
        tekst: `${NIETS_TE_DOEN} deze waarde zit al in de regel 'Participatieladder'.`,
        link: null,
    }),
    'geen-bedragen': () => ({
        tekst: `${NIETS_TE_DOEN} de Meetstandaard heeft voor dit effect nog geen bedragen. Ze komen mee met een nieuwe versie.`,
        link: null,
    }),
    'versies-gemengd': () => ({ tekst: `De vragen van dit effect komen uit verschillende versies van de Meetstandaard. ${NAAR_DECCOS}`, link: null }),
    'versie-ontbreekt': () => ({ tekst: `Bij de vragen van dit effect staat niet uit welke versie van de Meetstandaard ze komen. ${NAAR_DECCOS}`, link: null }),
    'versie-onbekend': () => ({ tekst: `De vragen van dit effect komen uit een versie van de Meetstandaard die Deccos nog niet kent. ${NAAR_DECCOS}`, link: null }),
    'nog-niet-gemeten': (client) => ({
        tekst: "Zet het onderzoek in dit boekjaar uit: 'Meten' → 'Onderzoeken', en klik op het verzendicoon bij het onderzoek.",
        link: naarOnderzoeken(client),
    }),
    'een-meetmoment': (client) => ({
        tekst: 'Laat de vragenlijst in dit boekjaar ook op een tweede meetmoment invullen: een nulmeting én een vervolgmeting.',
        link: naarOnderzoeken(client),
    }),
    'geen-deelnemer-twee-momenten': (client) => ({
        tekst: 'Deel de vragenlijst met de deelnemer erin, zodat zijn antwoorden op beide meetmomenten bij hem horen: '
            + "vanaf het tabblad 'Onderzoeken' van de deelnemer, of via 'Meten' → 'Onderzoeken' met 'Selecteer deelnemer of huishouden' "
            + "op de pagina 'Vragenlijst delen'.",
        link: naarOnderzoeken(client),
    }),

    // --- Status van het kengetal (bepaalStatus) ---
    'te-weinig-metingen': () => ({
        tekst: "Bij 'Gekoppelde metingen' → 'Telt niet mee' hieronder staat per regel wat je kunt aanvullen.",
        link: null,
    }),
    'periode-te-kort': () => ({
        tekst: `${NIETS_TE_DOEN} het kengetal verschijnt zodra het boekjaar zes volle maanden loopt.`,
        link: null,
    }),
    'geen-boekjaar': (client) => ({
        tekst: "Controleer het jaar bij 'Instellingen' → tabblad 'Algemeen' → 'Kosten per jaar': het boekjaar mag niet in de toekomst liggen.",
        link: naarKosten(client),
    }),
    'geen-kosten': (client) => ({
        tekst: "Vul de kosten van het boekjaar in bij 'Instellingen' → tabblad 'Algemeen' → 'Kosten per jaar'.",
        link: naarKosten(client),
    }),
}

/** Alle codes met een advies. */
const REDEN_CODES = Object.keys(ADVIES)

/**
 * @param {string|null|undefined} code - reasonCode of redenCode
 * @param {string} client - CompagnyID, voor de link
 * @returns {Advies|null} null bij een onbekende code — liever geen advies dan een verzonnen
 */
const koppelingAdvies = (code, client) => (code && ADVIES[code] ? ADVIES[code](client) : null)

/**
 * @typedef {object} RedenGroep
 * @property {string} key
 * @property {string} reason - De reden zoals de regels hem noemen
 * @property {string|null} reasonCode
 * @property {Array<object>} rijen - De regels met deze reden, in hun volgorde
 */

/**
 * Zet regels uit 'Telt niet mee' met dezelfde reden onder één kop, zodat het
 * advies één keer per groep staat. Gegroepeerd op de redentekst: twee
 * samengevoegde effecten met een ander doel ("via X", "via Y") blijven apart.
 *
 * @param {Array<{key: string, reason: string, reasonCode?: string|null}>} [rijen]
 * @returns {Array<RedenGroep>}
 */
const groepeerNietMeetellend = (rijen = []) => {
    const groepen = []
    const opReden = new Map()
    ;(Array.isArray(rijen) ? rijen : []).forEach((rij) => {
        if (!opReden.has(rij.reason)) {
            const groep = { key: `groep-${rij.key}`, reason: rij.reason, reasonCode: rij.reasonCode ?? null, rijen: [] }
            opReden.set(rij.reason, groep)
            groepen.push(groep)
        }
        opReden.get(rij.reason).rijen.push(rij)
    })
    return groepen
}

export default koppelingAdvies
export { groepeerNietMeetellend, REDEN_CODES }
